import { execute } from '@/graphql/execute';
import { UploadType } from '@/graphql/graphql';
import { createAttachment, deleteAttachment } from '@/queries/attachment.query';
import { useState } from 'react';

interface UseImageUploadProps {
  organizationId?: string;
  organizationName?: string;
  type: UploadType;
}

export function useImageUpload({ organizationId, type }: UseImageUploadProps) {
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  const checksumSHA256Base64 = async (file: File): Promise<string | null> => {
    // chrome does not support files larger than 2GB in arrayBuffer
    if (file.size > 2 * 1024 * 1024 * 1024) return null;
    const buffer = await file.arrayBuffer();
    const hashBuffer = await crypto.subtle.digest('SHA-256', buffer);
    const byteArray = new Uint8Array(hashBuffer);
    let binary = '';
    for (let i = 0; i < byteArray.length; i++) {
      binary += String.fromCharCode(byteArray[i]);
    }
    return btoa(binary);
  };

  const uploadImage = async (file: File): Promise<string> => {
    setIsUploading(true);
    setUploadProgress(0);

    try {
      // Generate a unique filename for organization logo
      const timestamp = Date.now();
      const fileExtension = file.name.split('.').pop() || 'jpg';
      const filename = `organization-${organizationId}-logo-${timestamp}.${fileExtension}`;

      // Generate checksum
      const checksum = await checksumSHA256Base64(file);
      if (!checksum) {
        throw new Error('Failed to generate file checksum');
      }

      setUploadProgress(25);

      // Create attachment and get upload URL
      const data = await execute(createAttachment, {
        filename,
        contentType: file.type || 'image/jpeg',
        checksumSHA256Base64: checksum,
        type,
      });

      const { attachment, uploadUrl } = data?.createAttachment || {};
      if (!attachment?.id || !uploadUrl) {
        throw new Error('Failed to create attachment - no attachment ID or upload URL received');
      }

      setUploadProgress(50);

      // Upload the file directly to the presigned URL
      const uploadResponse = await fetch(uploadUrl, {
        method: 'PUT',
        body: file,
        headers: {
          'Content-Type': file.type || 'image/jpeg',
          'X-Amz-Checksum-Sha256': checksum,
        },
      });

      if (!uploadResponse.ok) {
        throw new Error(`Failed to upload file: ${uploadResponse.status} ${uploadResponse.statusText}`);
      }

      setUploadProgress(100);

      return attachment.id;
    } catch (error) {
      console.error('Image upload error:', error);
      throw new Error(`Failed to upload image: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
    }
  };

  const deleteImage = async (attachmentId: string): Promise<void> => {
    try {
      await execute(deleteAttachment, { attachmentId });
    } catch (error) {
      console.error('Image deletion error:', error);
      throw new Error(`Failed to delete image: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  return {
    uploadImage,
    deleteImage,
    isUploading,
    uploadProgress,
  };
}
